import { useQuery } from "@tanstack/react-query";
import { productsService } from "@/services/productsService";
import type { ProductSearchParams } from "@/types/product.dto";
import {
  mapProductListDtosToStorefront,
  mapProductListDtoToStorefront,
  mapProductResponseDtoToStorefront,
} from "@/lib/mappers/storefront-mappers";
import { queryKeys } from "./keys";

export function useProductSearch(params: ProductSearchParams, enabled = true) {
  return useQuery({
    queryKey: queryKeys.products.list(params),
    queryFn: () => productsService.search(params),
    enabled,
  });
}

export function useStorefrontProductSearch(params: ProductSearchParams, enabled = true) {
  return useQuery({
    queryKey: queryKeys.products.list(params),
    queryFn: async () => {
      const result = await productsService.search(params);
      return {
        ...result,
        items: mapProductListDtosToStorefront(result.items),
      };
    },
    enabled,
  });
}

export function useFeaturedProducts(limit = 10) {
  return useQuery({
    queryKey: queryKeys.products.featured(limit),
    queryFn: async () => {
      const items = await productsService.getFeatured(limit);
      return mapProductListDtosToStorefront(items);
    },
  });
}

export function useProductById(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.products.detail(id),
    queryFn: () => productsService.getById(id),
    enabled: enabled && id > 0,
  });
}

export function useStorefrontProductById(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.products.detail(id),
    queryFn: async () => {
      const product = await productsService.getById(id);
      return mapProductResponseDtoToStorefront(product);
    },
    enabled: enabled && id > 0,
  });
}

export function useStoreProducts(storeId: number, page = 1, pageSize = 50, enabled = true) {
  return useQuery({
    queryKey: queryKeys.products.byStore(storeId, page, pageSize),
    queryFn: async () => {
      const result = await productsService.getByStore(storeId, page, pageSize);
      return {
        ...result,
        items: mapProductListDtosToStorefront(result.items),
      };
    },
    enabled: enabled && storeId > 0,
  });
}

export function useCategoryProducts(categoryId: number, page = 1, pageSize = 50, enabled = true) {
  return useQuery({
    queryKey: queryKeys.products.byCategory(categoryId, page, pageSize),
    queryFn: async () => {
      const result = await productsService.getByCategory(categoryId, page, pageSize);
      return {
        ...result,
        items: mapProductListDtosToStorefront(result.items),
      };
    },
    enabled: enabled && categoryId > 0,
  });
}

export { mapProductListDtoToStorefront, mapProductResponseDtoToStorefront };
